"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "./auth-context";
import { isAdmin, isAdminOrManager } from "./role-helpers";
import { usePermissions, type ModuleRequirement } from "@/lib/permissions";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireManagerOrAdmin?: boolean;
  /** One module key, or a list of keys of which any one grants access. */
  requiredModule?: ModuleRequirement;
}

export function ProtectedRoute({
  children,
  requireAdmin = false,
  requireManagerOrAdmin = false,
  requiredModule,
}: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated, requiresLegalAcceptance } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        router.push("/auth");
      } else if (requiresLegalAcceptance) {
        // Issue #143 — gate ANY protected route on combined legal acceptance.
        // The login + 2FA paths already push /auth/tof on success, but a user
        // with a valid existing token + DPN=false (e.g. stale tab on deploy,
        // or a user who hit deprecated /accept-tof) must be redirected too.
        router.push("/auth/tof");
      } else if (requireAdmin && user && !isAdmin(user)) {
        router.push("/dashboard");
      } else if (requireManagerOrAdmin && user && !isAdminOrManager(user)) {
        router.push("/dashboard");
      }
    }
  }, [
    isLoading,
    isAuthenticated,
    requiresLegalAcceptance,
    user,
    requireAdmin,
    requireManagerOrAdmin,
    router,
  ]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Issue #143 — suppress protected children while legal acceptance is
  // pending; the effect above is redirecting to /auth/tof.
  if (requiresLegalAcceptance) {
    return null;
  }

  if (requireAdmin && user && !isAdmin(user)) {
    return null;
  }

  if (requireManagerOrAdmin && user && !isAdminOrManager(user)) {
    return null;
  }

  if (requiredModule) {
    return <ModuleGate moduleKey={requiredModule}>{children}</ModuleGate>;
  }

  return <>{children}</>;
}

/**
 * Inner gate component that checks module permissions.
 * Must be rendered inside a PermissionsProvider.
 */
function ModuleGate({
  moduleKey,
  children,
}: {
  moduleKey: ModuleRequirement;
  children: React.ReactNode;
}) {
  const { hasModule, isLoading } = usePermissions();
  const router = useRouter();
  // Resolved once per render, so the effect depends on the answer rather than
  // on the requirement's identity: a list written inline at the call site is a
  // new array on every render and would otherwise re-run the effect each time.
  const allowed = hasModule(moduleKey);

  useEffect(() => {
    if (!isLoading && !allowed) {
      router.push("/dashboard");
    }
  }, [isLoading, allowed, router]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Checking permissions...</p>
        </div>
      </div>
    );
  }

  if (!allowed) {
    return null;
  }

  return <>{children}</>;
}
